import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'

import en from '../../locales/en.json'
import it from '../../locales/it.json'

export const SUPPORTED_LOCALES = ['it', 'en'] as const
export type AppLocale = (typeof SUPPORTED_LOCALES)[number]

export const DEFAULT_LOCALE: AppLocale = 'it'
export const LOCALE_STORAGE_KEY = 'kuniri.locale'

export const resources = {
  it: { translation: it },
  en: { translation: en },
} as const

export function isAppLocale(value: string | null | undefined): value is AppLocale {
  return value === 'it' || value === 'en'
}

export function detectLocale(
  languages: readonly string[] | undefined,
): AppLocale {
  for (const language of languages ?? []) {
    const normalized = language.trim().toLowerCase()
    if (normalized.startsWith('it')) return 'it'
    if (normalized.startsWith('en')) return 'en'
  }
  return DEFAULT_LOCALE
}

export function getStoredLocale(): AppLocale | null {
  if (typeof window === 'undefined') return null
  try {
    const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY)
    return isAppLocale(stored) ? stored : null
  } catch {
    return null
  }
}

export function resolveInitialLocale(): AppLocale {
  const stored = getStoredLocale()
  if (stored) return stored
  if (typeof navigator === 'undefined') return DEFAULT_LOCALE
  return detectLocale(navigator.languages ?? [navigator.language])
}

function applyDocumentLanguage(locale: AppLocale): void {
  if (typeof document !== 'undefined') {
    document.documentElement.lang = locale
  }
}

void i18n.use(initReactI18next).init({
  resources,
  lng: resolveInitialLocale(),
  fallbackLng: DEFAULT_LOCALE,
  supportedLngs: [...SUPPORTED_LOCALES],
  defaultNS: 'translation',
  interpolation: { escapeValue: false },
  returnNull: false,
  initAsync: false,
})

applyDocumentLanguage(isAppLocale(i18n.language) ? i18n.language : DEFAULT_LOCALE)

/**
 * Persists the explicit choice in `kuniri.locale` and updates the document
 * language. Authenticated callers should also PATCH `/me {locale}`; the
 * profile value wins across devices (D-038).
 */
export async function setLocale(
  locale: AppLocale,
  { persist = true }: { persist?: boolean } = {},
): Promise<void> {
  await i18n.changeLanguage(locale)
  applyDocumentLanguage(locale)
  if (!persist || typeof window === 'undefined') return
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale)
  } catch {
    // Private mode may deny storage; the in-memory locale still applies.
  }
}

export function currentLocale(): AppLocale {
  return isAppLocale(i18n.language) ? i18n.language : DEFAULT_LOCALE
}

export default i18n
